import {
  Alert,
  AlertTitle,
  Button,
  Grid,
  ImageList,
  ImageListItem,
  Typography,
} from '@mui/material';
import { useGoogleLogin } from '@react-oauth/google';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import bureauImg from '../../../images/bureaux.jpg';
import logo from '../../../images/logo-hoortrade.jpg';

import { RocketLaunch } from '@mui/icons-material';
import { format } from 'date-fns';
import AxiosConfig from '../../../config/AxiosConfig';
import {
  checkAccessAdministrator,
  checkAccessLeader,
  checkAccessResponsible,
  checkAccessUser,
  checkRoleFi,
} from '../../helpers/utils/RoleManager';
import useSystemStatus from '../../hooks/system/useSystemStatus';
import { setSystem } from '../../states/app/staticSlice';
import { setProfile, setTry } from '../../states/login/loginSlice';
import PandoraIcon from '../svg-icons/PandoraIcon.jsx';

const LoginAlert = ({ open, handleClose }) => {
  if (!open) {
    return null;
  }
  return (
    <Alert severity="error" onClose={handleClose}>
      <AlertTitle>Problème de connexion</AlertTitle>
      Vos droits ne vous permettent pas l&apos;ccès à cette plateforme. Merci de
      contacter votre administrateur !
    </Alert>
  );
};

const LoginImage = ({ src, alt }) => (
  <ImageList
    sx={{
      width: '50vw',
      height: '100vh',
      objectFit: 'cover',
      padding: 0,
      margin: 0,
      overflow: 'hidden',
    }}
  >
    <ImageListItem
      sx={{
        width: '210%',
      }}
    >
      <img src={src} alt={alt} loading="lazy" />
    </ImageListItem>
  </ImageList>
);

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const profile = useSelector((state) => state?.login?.profile);
  const tryLogin = useSelector((state) => state?.login?.try);
  const [user, setUser] = useState(null);
  const [open, setOpen] = useState(false);
  const [newAttempt, setNewAttempt] = useState(false);
  const { system } = useSystemStatus();

  const handleLogin = useGoogleLogin({
    onSuccess: (codeResponse) => setUser(codeResponse),
    onError: (error) => console.error('Login Failed:', error),
  });

  useEffect(() => {
    if (user && user?.access_token) {
      AxiosConfig.create('get')
        .googleGetResponse(user)
        .then(async (res) => {
          return await AxiosConfig.create('post', '/users/login').login(
            {
              ...res?.data,
            },
            user
          );
        })
        .then((res) => {
          dispatch(setTry(true));
          dispatch(setProfile(res?.data?.user));
          dispatch(setSystem(system));
          setNewAttempt(true);
        })
        .catch((err) => console.error(err));
    }
  }, [user]);

  useEffect(() => {
    if (newAttempt) {
      if (profile && profile?.isActive === true) {
        setOpen(false);
        const redirectTo = new URLSearchParams(location.search).get(
          'redirectTo'
        );
        if (redirectTo?.length && redirectTo !== '/login') {
          return navigate(redirectTo);
        }
        if (checkAccessAdministrator(profile)) {
          dispatch(setTry(false));
          navigate('/admin');
        } else if (
          checkAccessLeader(profile) ||
          checkAccessResponsible(profile)
        ) {
          dispatch(setTry(false));
          navigate('/dashboard-sc');
        } else if (checkRoleFi(profile)) {
          dispatch(setTry(false));
          navigate('/dashboard-fi');
        } else if (checkAccessUser(profile)) {
          dispatch(setTry(false));
          navigate('/inbox');
        } else {
          dispatch(setTry(true));
          dispatch(setProfile(null));
          setNewAttempt(false);
        }
      } else if (profile === null && tryLogin) {
        setOpen(true);
        setTimeout(handleClose, 30000);
      }
    }
  }, [profile, newAttempt]);

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <LoginAlert open={open} handleClose={handleClose} />
      <Grid container>
        <Grid
          container
          direction="column"
          justifyContent="space-between"
          alignItems="center"
          sx={{ height: '100vh', padding: 2, width: '50%' }}
        >
          <Grid
            container
            direction="column"
            alignItems="start"
            sx={{
              width: '100%',
              marginTop: '20%',
            }}
          >
            <Grid
              container
              direction="column"
              alignItems="start"
              sx={{
                width: '100%',
                marginLeft: '10%',
              }}
            >
              <PandoraIcon
                style={{
                  width: '120px',
                  height: '120px',
                  marginLeft: '2.5em',
                  marginBottom: '0.25em',
                }}
              />
              <Typography
                component={'span'}
                variant="h2"
                sx={{
                  marginBottom: '1rem',
                  fontWeight: 'bold',
                }}
              >
                PANDORA
              </Typography>
              <Typography
                variant={'subtitle1'}
                sx={{
                  fontWeight: 'bold',
                  fontSize: '1.25rem',
                  marginBottom: '0.2em',
                }}
              >
                Bienvenue sur votre CRM
              </Typography>
              {system?.status?.code === 'DEPLOYING' ? (
                <Alert
                  icon={<RocketLaunch />}
                  severity="warning"
                  variant="outlined"
                  style={{ marginTop: '1rem', fontSize: '1rem' }}
                >
                  <Typography
                    variant={'subtitle2'}
                    sx={{ fontSize: '0.9em', fontWeight: 'bold' }}
                  >
                    Déploiement en cours.
                    {system?.estimedEndAt
                      ? ` Fin estimée à: ${format(system?.estimedEndAt, 'dd/MM/yyyy HH:mm')}`
                      : null}
                  </Typography>
                </Alert>
              ) : (
                <Typography variant={'subtitle2'} sx={{ fontSize: '0.9em' }}>
                  Accès autorisé aux comptes <strong>@hoortrade.com</strong>{' '}
                  uniquement
                </Typography>
              )}
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleLogin}
                sx={{ marginTop: '2.5rem' }}
              >
                Se connecter
              </Button>
            </Grid>
          </Grid>
          <div
            style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <ImageList
              cols={1}
              sx={{
                marginTop: '2rem',
                width: '180px',
              }}
            >
              <ImageListItem>
                <img
                  src={logo}
                  alt={`Hoortrade : Casanoov - Cazeboo - Sicaan`}
                  loading="lazy"
                />
              </ImageListItem>
            </ImageList>
            <Grid
              container
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ marginTop: '0.5rem' }}
            >
              <Typography
                component={'span'}
                variant="body1"
                sx={{ fontSize: '0.8rem' }}
              >
                Cette plateforme est réservée aux utilisateurs de
                l&apos;entreprise Hoortrade.
              </Typography>
              <Typography
                component={'span'}
                variant="body1"
                sx={{ fontSize: '0.8rem' }}
              >
                Version : {import.meta.env.VITE_PROJECT_VERSION}
              </Typography>
            </Grid>
          </div>
        </Grid>
        <Grid>
          <LoginImage src={bureauImg} alt="home-image" />
        </Grid>
      </Grid>
    </>
  );
};

export default Login;
